import type { EditImageSaveTarget } from '@/types/edit-image';
import {
  useGenerationStore,
  type ProcessingPreviewItem,
  type StartGenerationTaskPayload,
} from '@/stores/generationStore';
import { readTrackedImageEditTasks } from '@/lib/image-edit-task-tracker';

const PREVIEW_LOADING_DESCRIPTION = '生成任务已提交，完成后将自动更新当前预览';

export interface ImageEditPreviewLoadingContext {
  projectId?: string | null;
  resourceId?: string | null;
  previewItemId?: string | null;
  aspectRatio?: number;
  previousAssetUrl?: string | null;
}

type ImageEditPreviewStartInput = {
  target: EditImageSaveTarget;
  context: ImageEditPreviewLoadingContext;
};

function resolveResourcePreviewMode(
  resourceType: 'character' | 'location' | 'prop'
): Pick<StartGenerationTaskPayload, 'mode' | 'type' | 'title'> {
  switch (resourceType) {
    case 'character':
      return { mode: 'character-image', type: 'character', title: '角色图生成中' };
    case 'location':
      return { mode: 'location-image', type: 'location', title: '场景图生成中' };
    case 'prop':
      return { mode: 'prop-image', type: 'prop', title: '道具图生成中' };
    default: {
      const exhaustive: never = resourceType;
      return exhaustive;
    }
  }
}

export function buildImageEditPreviewStartPayload(
  input: ImageEditPreviewStartInput
): StartGenerationTaskPayload | null {
  const projectId = input.context.projectId?.trim();
  if (!projectId) return null;

  const previousAssetUrl = input.context.previousAssetUrl ?? null;
  const common = {
    projectId,
    prompt: '',
    description: PREVIEW_LOADING_DESCRIPTION,
    thumbnailUrl: previousAssetUrl ?? '',
    previousAssetUrl,
    previousThumbnailUrl: previousAssetUrl,
    assetType: 'image' as const,
    aspectRatio: input.context.aspectRatio,
    origin: 'image-edit' as const,
    affectsLimiter: false,
  };

  if (input.target.kind === 'storyboard-image') {
    return {
      ...common,
      mode: 'storyboard-image',
      storyboardId: input.target.storyboardId,
      resourceId: null,
      tagId: input.target.planId,
      previewItemId: input.context.previewItemId ?? input.target.sourceImageId,
      title: '分镜生成中',
      type: 'storyboard',
    };
  }

  if (input.target.kind !== 'project-resource') {
    return null;
  }

  const resourceId = input.context.resourceId?.trim();
  const elementTagId = input.target.elementTagId.trim();
  if (!resourceId || !elementTagId) return null;

  const mapped = resolveResourcePreviewMode(input.target.resourceType);
  return {
    ...common,
    ...mapped,
    storyboardId: null,
    resourceId,
    tagId: elementTagId,
    previewItemId: input.context.previewItemId ?? null,
  };
}

export function shouldSkipImageEditPreviewStart(
  processingItems: ReadonlyArray<
    Pick<ProcessingPreviewItem, 'mode' | 'storyboardId' | 'resourceId' | 'tagId'>
  >,
  payload: StartGenerationTaskPayload
): boolean {
  return processingItems.some(
    (item) =>
      item.mode === payload.mode &&
      item.tagId === payload.tagId &&
      (payload.mode === 'storyboard-image' || payload.mode === 'storyboard-video'
        ? item.storyboardId === payload.storyboardId
        : item.resourceId === payload.resourceId)
  );
}

export function isImageEditPreviewTask(task: {
  origin?: 'image-edit' | 'image-upscale' | 'image-split';
  jobId?: string;
}): boolean {
  if (task.origin === 'image-edit') return true;
  if (!task.jobId) return false;
  return readTrackedImageEditTasks().some((item) => item.jobId === task.jobId);
}

export function startImageEditPreviewLoading(input: {
  jobId: string;
  target: EditImageSaveTarget;
  context: ImageEditPreviewLoadingContext;
}): void {
  const payload = buildImageEditPreviewStartPayload(input);
  if (!payload) return;
  const store = useGenerationStore.getState();
  if (shouldSkipImageEditPreviewStart(store.processingItems, payload)) return;
  const started = store.startTask(payload);
  if (!started) return;
  store.bindJobId(started.taskId, input.jobId);
  store.releaseTaskSlot(started.taskId);
}
